import React, { useEffect, useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import RenterLayout from '../components/layout/RenterLayout';
import CarCard from '../components/ui/CarCard';
import { renterService } from '../services/renter.service';
import { showAppAlert } from '../utils/appAlert';

const RenterWishlist = () => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await renterService.getWishlist();
        setWishlist(response || []);
      } catch (err) {
        setError(err.message || 'Failed to load your wishlist.');
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, []);

  const handleRemoveFromWishlist = async (carId) => {
    try {
      await renterService.removeFromWishlist(carId);
      setWishlist((current) => current.filter((car) => car.id !== carId));
      showAppAlert({
        type: 'success',
        title: 'Removed from wishlist',
        message: 'The vehicle has been removed from your saved cars.'
      });
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Wishlist update failed',
        message: error.message || 'We could not update your wishlist.'
      });
    }
  };

  return (
    <RenterLayout
      activePage="Wishlist"
      title="Saved Cars"
      description="Keep your favorite vehicles close so you can compare them and book faster later."
      actions={
        <Link
          to="/browse"
          className="inline-flex items-center gap-2 rounded-2xl bg-brand-black px-5 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white hover:bg-black transition-colors"
        >
          Browse More Cars
        </Link>
      }
    >
      {loading ? (
        <div className="min-h-[360px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="rounded-[1.75rem] border border-red-100 bg-red-50 px-6 py-5 text-red-700 font-semibold">
          {error}
        </div>
      ) : wishlist.length === 0 ? (
        <div className="rounded-[1.75rem] border border-gray-100 bg-[#f8fafc] px-6 py-16 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
            <Heart className="w-7 h-7 text-gray-400" />
          </div>
          <h2 className="text-2xl font-black tracking-tight mb-2">Your wishlist is still empty</h2>
          <p className="text-sm text-gray-500 font-medium max-w-xl mx-auto leading-6 mb-6">
            Save vehicles you like while browsing and they will appear here for quick access later.
          </p>
          <Link
            to="/browse"
            className="inline-flex items-center gap-2 rounded-2xl bg-brand-black px-5 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white hover:bg-black transition-colors"
          >
            Explore Fleet
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {wishlist.map((car) => (
            <CarCard
              key={car.id}
              car={car}
              isWishlisted
              onToggleWishlist={() => handleRemoveFromWishlist(car.id)}
            />
          ))}
        </div>
      )}
    </RenterLayout>
  );
};

export default RenterWishlist;
